import Box from "@mui/material/Box";
import { colors } from "@/theme/colors";
import { fonts } from "@/theme/tokens";

/** Traffic-light dot in the window chrome. */
const dot = (c: string) => ({
  width: 11,
  height: 11,
  borderRadius: "50%",
  backgroundColor: c,
});

/** Relative bar heights (%) for the little activity chart; the last is live. */
const BARS = [34, 48, 40, 58, 52, 70, 60, 82, 74, 92];

const STATS: readonly (readonly [string, string])[] = [
  ["Revenue", "$48.2k"],
  ["Users", "12,904"],
  ["Uptime", "99.9%"],
];

const reduce = { "@media (prefers-reduced-motion: reduce)": { animation: "none" } };

/**
 * A realistic-looking product dashboard inside a desktop browser window —
 * chrome bar, sidebar, stat tiles, and an activity chart whose bars rise on
 * entrance. Pure DOM/CSS on the site palette; decorative only.
 */
export function BrowserMock() {
  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: colors.paper,
        border: `1px solid ${colors.divider}`,
        boxShadow:
          "0 34px 60px -22px rgba(17,24,39,0.30), 0 12px 22px -14px rgba(17,24,39,0.18)",
        "@keyframes heroBarRise": {
          from: { transform: "scaleY(0.12)" },
          to: { transform: "scaleY(1)" },
        },
        "@keyframes heroLive": {
          "0%, 100%": { opacity: 1 },
          "50%": { opacity: 0.25 },
        },
      }}
    >
      {/* Window chrome. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.75,
          height: 38,
          borderBottom: `1px solid ${colors.divider}`,
        }}
      >
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <Box sx={dot("#f87171")} />
          <Box sx={dot("#fbbf24")} />
          <Box sx={dot("#34d399")} />
        </Box>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              height: 20,
              minWidth: "48%",
              borderRadius: "999px",
              backgroundColor: colors.background,
              border: `1px solid ${colors.divider}`,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 7,
                borderRadius: "2px 2px 0 0",
                border: `1.4px solid ${colors.secondary}`,
                borderBottom: "none",
              }}
            />
            <Box
              component="span"
              sx={{
                fontFamily: fonts.mono,
                fontSize: "0.6rem",
                color: colors.textSecondary,
                letterSpacing: "0.04em",
              }}
            >
              studio.build
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: 44 }} />
      </Box>

      {/* Body: sidebar + main panel. */}
      <Box sx={{ display: "flex", height: { xs: 210, md: 296 } }}>
        <Box
          sx={{
            width: { xs: 54, md: 66 },
            borderRight: `1px solid ${colors.divider}`,
            p: 1.25,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "6px",
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.boost})`,
              mb: 0.5,
            }}
          />
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                height: 8,
                borderRadius: 1,
                backgroundColor: i === 1 ? colors.primary : "#e5e7eb",
                width: i === 1 ? "100%" : "72%",
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            flex: 1,
            p: { xs: 1.5, md: 2 },
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: fonts.heading,
                  fontSize: { xs: "0.85rem", md: "1rem" },
                  fontWeight: 500,
                  color: colors.textPrimary,
                }}
              >
                Overview
              </Box>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: colors.success,
                  animation: "heroLive 2.4s ease-in-out infinite",
                  ...reduce,
                }}
              />
            </Box>
            <Box
              sx={{
                px: 1.25,
                height: 20,
                borderRadius: "5px",
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.boost})`,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: fonts.mono,
                  fontSize: "0.55rem",
                  color: "#fff",
                  letterSpacing: "0.08em",
                }}
              >
                DEPLOY
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 1,
            }}
          >
            {STATS.map(([label, val]) => (
              <Box
                key={label}
                sx={{
                  p: 1,
                  borderRadius: "6px",
                  border: `1px solid ${colors.divider}`,
                  backgroundColor: colors.background,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontFamily: fonts.mono,
                    fontSize: "0.5rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: colors.textSecondary,
                  }}
                >
                  {label}
                </Box>
                <Box
                  sx={{
                    fontFamily: fonts.heading,
                    fontSize: { xs: "0.72rem", md: "0.95rem" },
                    fontWeight: 500,
                    color: colors.textPrimary,
                    mt: 0.25,
                  }}
                >
                  {val}
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              flex: 1,
              border: `1px solid ${colors.divider}`,
              borderRadius: "6px",
              p: 1.25,
              display: "flex",
              alignItems: "flex-end",
              gap: { xs: 0.5, md: 0.75 },
            }}
          >
            {BARS.map((h, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: `${h}%`,
                  borderRadius: "3px 3px 0 0",
                  transformOrigin: "bottom",
                  background:
                    i === BARS.length - 1
                      ? `linear-gradient(180deg, ${colors.primary}, ${colors.boost})`
                      : "#ece7f6",
                  animation: `heroBarRise 0.6s ease-out ${i * 0.05}s both`,
                  ...reduce,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
